/**
 * useBuddyDialogue — 伴侣情境对话状态机（工单 5）
 *
 * 只感知三件事：伴侣自身状态、神枢 CLI 审批事件、iCloser 金库状态。
 * 触发链：
 *   Onload          → anim="drop"       + 入场文案，2s 后回 idle
 *   Hover KernelHub → anim="hold-shield" + 文案 2（hold-* 跟随悬停）
 *   Hover Vault     → anim="hold-chest"  + 文案 3（hold-* 跟随悬停）
 *   Spam Click      → 3s 内点击 ≥3 次   → anim="angry" + 文案 4，2s 后回 idle
 *   Idle > 15s      → anim="sleep"      + 文案 5，任何交互复位为 idle
 *   xin:bubble 事件  → 神枢 CLI 审批事件：自定义文案 + detail.anim，约 3s 后回 idle
 *
 * 悬停/戳弄 300-500ms 节流（trailing 补发最新意图），避免气泡疯狂切换。
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  BUDDY_LINES,
  CUSTOM_LINE_DURATION_MS,
  IDLE_MS,
  LINE_DURATION_MS,
  makeCustomLine,
  type BuddyLine,
  type BuddyMood,
} from "@/lib/buddyLines";
import {
  BUDDY_EXPRESSIONS,
  pickLineByTrigger,
  type ExpressionId,
  type VoiceLine,
  type VoiceTrigger,
} from "@/lib/buddyVoice";

/**
 * 动效契约 —— 与 BuddyXin 侧共享，一字不差。
 * 优先本地声明同名同义 union，避免并行开发中 buddyEngine.ts 的竞态；
 * 待 buddyEngine 稳定导出后可改 `import type { BuddyAnim } from "@/lib/buddyEngine"`。
 * 九态（calm…trance）与 buddyStates.BuddyStateId 对齐，命名镜像 buddyEngine。
 */
export type BuddyAnim =
  | "idle" // 默认待机
  | "drop" // 入场从天而降
  | "pet" // 摸头（脸红爱心/叉腰）
  | "angry" // 连续戳弄发怒
  | "sleep" // 闲置打瞌睡
  | "hold-shield" // 悬停神枢：掏对讲机/举警戒盾
  | "hold-chest" // 悬停钱柜：高举黄金宝箱
  // ── 九态拟人状态（镜像 buddyEngine / buddyStates）──
  | "calm"
  | "joy"
  | "focus"
  | "alert"
  | "rage"
  | "fear"
  | "fatigue"
  | "sorrow"
  | "trance";

export type { BuddyLine, BuddyMood };

/** 瞬时动效（drop/pet/angry）保持后自动回 idle */
const TRANSIENT_ANIM_MS = 2_000;
/** hold-* 动效驻留（App 未接 mouseLeave 时的兜底回退；接上 onLeave* 则跟随悬停） */
const HOLD_MS = 6_000;
/** 悬停触发节流窗口：窗口内切换不同情境 → trailing 补发最新意图 */
const HOVER_THROTTLE_MS = 400;
/** Spam 戳弄判定：3 秒内 ≥3 次 */
const SPAM_WINDOW_MS = 3_000;
const SPAM_CLICKS = 3;

/** 情境 → 动效映射（与 BuddyXin 动效契约对齐） */
const MOOD_ANIM: Record<BuddyMood, BuddyAnim> = {
  onload: "drop",
  "hover-kernelhub": "hold-shield",
  "hover-vault": "hold-chest",
  "poke-spam": "angry",
  idle: "sleep",
};

/** 情境 → 动效驻留时长（null = 保持到显式复位，如 sleep 等交互唤醒） */
const MOOD_ANIM_HOLD_MS: Record<BuddyMood, number | null> = {
  onload: TRANSIENT_ANIM_MS,
  "hover-kernelhub": HOLD_MS,
  "hover-vault": HOLD_MS,
  "poke-spam": TRANSIENT_ANIM_MS,
  idle: null,
};

/** 情境 → 台词库触发场景（buddyVoice 轮换用） */
const MOOD_TRIGGER: Record<BuddyMood, VoiceTrigger> = {
  onload: "onload",
  "hover-kernelhub": "hover-kernelhub",
  "hover-vault": "hover-vault",
  "poke-spam": "poke-spam",
  idle: "idle",
};

/** 情境 → 表情 id（工单 5 原文行的 FX 面） */
const MOOD_EXPRESSION: Record<BuddyMood, ExpressionId> = {
  onload: "drop",
  "hover-kernelhub": "shield",
  "hover-vault": "chest",
  "poke-spam": "angry",
  idle: "sleep",
};

/** BuddyAnim（xin:bubble detail.anim）→ 表情 id */
const ANIM_EXPRESSION: Record<BuddyAnim, ExpressionId> = {
  idle: "talk",
  drop: "drop",
  pet: "pet",
  angry: "angry",
  sleep: "sleep",
  "hold-shield": "shield",
  "hold-chest": "chest",
  calm: "calm",
  joy: "joy",
  focus: "focus",
  alert: "alert",
  rage: "rage",
  fear: "fear",
  fatigue: "fatigue",
  sorrow: "sorrow",
  trance: "trance",
};

/** 台词库条目 → 气泡行（保持 BuddyLine 公开形状不变） */
function voiceToBuddyLine(v: VoiceLine, mood: BuddyMood): BuddyLine {
  return {
    id: mood,
    text: v.text,
    action: BUDDY_EXPRESSIONS[v.expression].labelZh,
  };
}

type HoverMood = "hover-kernelhub" | "hover-vault";

/** xin:bubble 事件 detail（神枢终端派发） */
interface XinBubbleDetail {
  text?: unknown;
  anim?: unknown;
}

const BUDDY_ANIMS: readonly string[] = [
  "idle",
  "drop",
  "pet",
  "angry",
  "sleep",
  "hold-shield",
  "hold-chest",
  "calm",
  "joy",
  "focus",
  "alert",
  "rage",
  "fear",
  "fatigue",
  "sorrow",
  "trance",
];

function isBuddyAnim(v: unknown): v is BuddyAnim {
  return typeof v === "string" && BUDDY_ANIMS.includes(v);
}

function clearTimer(r: { current: number | null }): void {
  if (r.current != null) {
    window.clearTimeout(r.current);
    r.current = null;
  }
}

export function useBuddyDialogue() {
  const [line, setLine] = useState<BuddyLine | null>(null);
  const [anim, setAnim] = useState<BuddyAnim>("drop");
  /** 表情浮层信号（BuddyFX 消费；key 自增强制重放） */
  const [fx, setFx] = useState<{ expression: ExpressionId; key: number } | null>(
    null,
  );

  // ── 定时器与状态镜像（让所有回调保持稳定引用） ──
  const animRef = useRef<BuddyAnim>("drop");
  /** 当前气泡的去重 key：标准情境为 mood，xin:bubble 为 `custom:${text}` */
  const lineKeyRef = useRef<string | null>(null);
  const hoverMoodRef = useRef<HoverMood | null>(null);
  const bubbleTimerRef = useRef<number | null>(null);
  const animTimerRef = useRef<number | null>(null);
  const idleTimerRef = useRef<number | null>(null);
  const hoverTrailingRef = useRef<number | null>(null);
  const pendingHoverRef = useRef<HoverMood | null>(null);
  const pokeTimesRef = useRef<number[]>([]);
  const lastHoverAtRef = useRef(0);
  /** 每个情境的出泡次数（首次出工单 5 原文，其后台词库轮换） */
  const moodFireCountRef = useRef<Partial<Record<BuddyMood, number>>>({});
  /** 各情境上一条台词库 id（轮换去重） */
  const lastVoiceIdRef = useRef<Partial<Record<BuddyMood, string>>>({});
  /** fx 自增键 */
  const fxKeyRef = useRef(0);

  /** 动效切换：holdMs 后自动回 idle；null 表示保持到显式复位 */
  const applyAnim = useCallback((next: BuddyAnim, holdMs: number | null) => {
    animRef.current = next;
    setAnim(next);
    clearTimer(animTimerRef);
    if (holdMs != null) {
      animTimerRef.current = window.setTimeout(() => {
        if (animRef.current === next) {
          animRef.current = "idle";
          setAnim("idle");
        }
      }, holdMs);
    }
  }, []);

  /** 出泡：同 key 仅续期不重打字（引用恒定 → 打字机不重启）；换 key 才换文案 */
  const showLine = useCallback(
    (next: BuddyLine, durationMs: number, key: string) => {
      const same = lineKeyRef.current === key;
      lineKeyRef.current = key;
      if (!same) setLine(next);
      clearTimer(bubbleTimerRef);
      bubbleTimerRef.current = window.setTimeout(() => {
        if (lineKeyRef.current === key) {
          lineKeyRef.current = null;
          setLine(null);
        }
      }, durationMs);
    },
    [],
  );

  /** 发射表情浮层信号（BuddyFX 重放） */
  const fireFx = useCallback((expression: ExpressionId) => {
    fxKeyRef.current += 1;
    setFx({ expression, key: fxKeyRef.current });
  }, []);

  /**
   * 按情境出泡（对话扩容）：每个情境首次出工单 5 原文（原文照用），
   * 其后从 buddyVoice 台词库按触发场景轮换（排除上一条防复读）。
   */
  const say = useCallback(
    (mood: BuddyMood) => {
      const count = moodFireCountRef.current[mood] ?? 0;
      moodFireCountRef.current[mood] = count + 1;
      if (count === 0) {
        showLine(BUDDY_LINES[mood], LINE_DURATION_MS[mood], mood);
        fireFx(MOOD_EXPRESSION[mood]);
        return;
      }
      const excludeId = lastVoiceIdRef.current[mood];
      const v = pickLineByTrigger(MOOD_TRIGGER[mood], { excludeId });
      lastVoiceIdRef.current[mood] = v.id;
      showLine(voiceToBuddyLine(v, mood), LINE_DURATION_MS[mood], mood);
      fireFx(v.expression);
    },
    [showLine, fireFx],
  );

  /** 收起气泡：指定 key 时仅当其仍在展示才收 */
  const dismissLine = useCallback((key?: string) => {
    if (key !== undefined && lineKeyRef.current !== key) return;
    if (lineKeyRef.current === null) return;
    lineKeyRef.current = null;
    setLine(null);
    clearTimer(bubbleTimerRef);
  }, []);

  /** 触发一个情境：文案 + 映射动效 */
  const fireMood = useCallback(
    (mood: BuddyMood) => {
      say(mood);
      applyAnim(MOOD_ANIM[mood], MOOD_ANIM_HOLD_MS[mood]);
    },
    [say, applyAnim],
  );

  /** 任何交互：复位闲置倒计时；sleep 唤醒回 idle 并收起打瞌睡文案 */
  const noteActivity = useCallback(() => {
    if (animRef.current === "sleep") {
      applyAnim("idle", null);
      dismissLine("idle");
    }
    clearTimer(idleTimerRef);
    idleTimerRef.current = window.setTimeout(() => {
      hoverMoodRef.current = null;
      pendingHoverRef.current = null;
      clearTimer(hoverTrailingRef);
      fireMood("idle");
    }, IDLE_MS);
  }, [applyAnim, dismissLine, fireMood]);

  // ── 悬停触发（400ms 节流，trailing 补发最新意图） ──
  const fireHover = useCallback(
    (mood: HoverMood) => {
      clearTimer(hoverTrailingRef);
      pendingHoverRef.current = null;
      lastHoverAtRef.current = Date.now();
      hoverMoodRef.current = mood;
      fireMood(mood);
    },
    [fireMood],
  );

  const triggerHover = useCallback(
    (mood: HoverMood) => {
      noteActivity();
      const elapsed = Date.now() - lastHoverAtRef.current;
      // 节流窗口内且非同情境续期 → 记录最新意图，窗口结束补发
      if (elapsed < HOVER_THROTTLE_MS && lineKeyRef.current !== mood) {
        pendingHoverRef.current = mood;
        clearTimer(hoverTrailingRef);
        hoverTrailingRef.current = window.setTimeout(
          () => {
            hoverTrailingRef.current = null;
            const m = pendingHoverRef.current;
            pendingHoverRef.current = null;
            if (m) fireHover(m);
          },
          HOVER_THROTTLE_MS - elapsed,
        );
        return;
      }
      fireHover(mood);
    },
    [noteActivity, fireHover],
  );

  /** 悬停结束：hold-* 跟随悬停回 idle；气泡留至自然淡出（CTA 仍可点） */
  const endHold = useCallback(
    (mood: HoverMood) => {
      if (hoverMoodRef.current !== mood) return;
      hoverMoodRef.current = null;
      if (pendingHoverRef.current === mood) {
        pendingHoverRef.current = null;
        clearTimer(hoverTrailingRef);
      }
      if (animRef.current === MOOD_ANIM[mood]) {
        applyAnim("idle", null);
      }
    },
    [applyAnim],
  );

  // ── 初始入场：从天而降 + 入场文案 ──
  useEffect(() => {
    animRef.current = "drop";
    setAnim("drop");
    const t = window.setTimeout(() => fireMood("onload"), 400);
    return () => window.clearTimeout(t);
  }, [fireMood]);

  // ── xin:bubble 事件协议：神枢 CLI 审批事件感知通道 ──
  // 神枢终端在 Ops/Destructive 高危权限切换时派发：
  //   window.dispatchEvent(new CustomEvent("xin:bubble",
  //     { detail: { text: "…", anim: "hold-shield" } }))
  // 收到即出泡（自定义文案 + detail.anim），约 3s 后回 idle。
  useEffect(() => {
    const onBuddyBubble = (e: Event) => {
      const detail = (e as CustomEvent<XinBubbleDetail>).detail;
      const raw = detail?.text;
      if (typeof raw !== "string" || raw.trim().length === 0) return;
      const text = raw.trim();
      const anim: BuddyAnim = isBuddyAnim(detail?.anim)
        ? detail.anim
        : "hold-shield";
      showLine(
        makeCustomLine(text),
        CUSTOM_LINE_DURATION_MS,
        `custom:${text}`,
      );
      applyAnim(anim, CUSTOM_LINE_DURATION_MS);
      fireFx(ANIM_EXPRESSION[anim]);
    };
    window.addEventListener("xin:bubble", onBuddyBubble);
    return () => window.removeEventListener("xin:bubble", onBuddyBubble);
  }, [showLine, applyAnim, fireFx]);

  // ── 全局交互监听（复位闲置倒计时） ──
  useEffect(() => {
    const events = ["pointerdown", "pointermove", "keydown"] as const;
    const onAny = () => noteActivity();
    events.forEach((e) => window.addEventListener(e, onAny, { passive: true }));
    noteActivity();
    return () => {
      events.forEach((e) => window.removeEventListener(e, onAny));
      clearTimer(idleTimerRef);
      clearTimer(bubbleTimerRef);
      clearTimer(animTimerRef);
      clearTimer(hoverTrailingRef);
    };
  }, [noteActivity]);

  // ── 对外事件句柄 ──
  const onHoverKernelHub = useCallback(
    () => triggerHover("hover-kernelhub"),
    [triggerHover],
  );
  const onHoverVault = useCallback(
    () => triggerHover("hover-vault"),
    [triggerHover],
  );
  const onLeaveKernelHub = useCallback(
    () => endHold("hover-kernelhub"),
    [endHold],
  );
  const onLeaveVault = useCallback(() => endHold("hover-vault"), [endHold]);

  /**
   * 戳弄：3s 内 ≥3 次判定 Spam → angry + 文案 4；
   * 非 spam 单戳 → pet 微动效（不打断气泡）；发怒中续戳延长 angry。
   */
  const onPoke = useCallback(() => {
    noteActivity();
    const now = Date.now();
    const win = pokeTimesRef.current.filter((t) => now - t < SPAM_WINDOW_MS);
    win.push(now);
    pokeTimesRef.current = win;

    if (win.length >= SPAM_CLICKS) {
      pokeTimesRef.current = [];
      fireMood("poke-spam");
      return;
    }
    if (animRef.current === "angry") {
      applyAnim("angry", TRANSIENT_ANIM_MS);
    } else {
      applyAnim("pet", TRANSIENT_ANIM_MS);
    }
  }, [noteActivity, fireMood, applyAnim]);

  return {
    line,
    anim,
    /** 表情浮层信号（新增字段，向后兼容：App 未接则无副作用） */
    fx,
    onPoke,
    onHoverKernelHub,
    onHoverVault,
    /** 可选对接：悬停区域 mouseLeave 时调用，hold-* 动效即时回 idle */
    onLeaveKernelHub,
    onLeaveVault,
  };
}
